import React, { useState } from 'react';
import { 
  EyeIcon,
  EyeSlashIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';

import toast from 'react-hot-toast';
import { LoadingSpinner } from '../common/Loading';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';


import { useAuth } from '../../context/AuthContext';


// Crown Icon Component
const CrownIcon = ({ size = 24, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M5 16L3 8l2.5 2L12 4l6.5 6L21 8l-2 8H5zm2.5-7L6 8l1 4h10l1-4-1.5 1L12 7 7.5 9z"/>
  </svg>
);

// Social Login Button Component
const SocialLoginButton = ({ icon, label, onClick, className = "" }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center justify-center py-3 px-4 border border-gray-600 rounded-lg text-white font-medium hover:bg-gray-700 transition-colors ${className}`}
  >
    {icon && <span className="mr-2">{icon}</span>}
    {label}
  </button>
);

// Admin Login Page Component
const AdminLoginPage = () => {
  const [formData, setFormData] = useState({
    email: 'billionaire@gmail.com',
    password: '12345678'
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');


  const { loginAdmin } = useAuth();
  const navigate = useNavigate();

 const handleSubmit = async () => {
  if (!formData.email || !formData.password) {
    toast.error('Please fill in all fields');
    return;
  }

  setIsLoading(true);
  setError('');

  // 3-second loading simulation
  setTimeout(async () => {
    try {
      const result = await loginAdmin(formData.email, formData.password);
      
      if (result.success) {
        toast.success('Admin login successful!');
        setFormData({ ...formData, password: '' }); // Clear password field
        
        setTimeout(() => {
          navigate('/admin');
        }, 1000);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Admin login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, 3000);
};

  const handleSocialLogin = (provider) => {
    console.log(`Logging in with ${provider}`);
    // Social login logic would go here
  };

  const handleForgotPassword = () => {
    console.log('Forgot password clicked');
    // Forgot password logic would go here
  };

  const handleCreateAccount = () => {
    console.log('Create account clicked');
    // Navigate to registration page
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-gray-900 to-black flex items-center justify-center p-4">
      {/* Floating Crown Icons */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <CrownIcon size={60} className="absolute top-20 left-20 text-yellow-500 opacity-20 animate-pulse" />
        <CrownIcon size={40} className="absolute top-40 right-32 text-yellow-500 opacity-15 animate-pulse" style={{ animationDelay: '1s' }} />
        <CrownIcon size={50} className="absolute bottom-32 left-40 text-yellow-500 opacity-10 animate-pulse" style={{ animationDelay: '2s' }} />
        <CrownIcon size={35} className="absolute bottom-20 right-20 text-yellow-500 opacity-25 animate-pulse" style={{ animationDelay: '0.5s' }} />
        <CrownIcon size={45} className="absolute top-60 left-1/2 text-yellow-500 opacity-20 animate-pulse" style={{ animationDelay: '1.5s' }} />
      </div>

      <div className="w-full max-w-md">
        {/* Login Card */}
        <div className="bg-gray-800 bg-opacity-90 backdrop-blur-sm border border-gray-700 rounded-2xl p-8 shadow-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="bg-gray-700 rounded-xl p-4">
                <CrownIcon size={40} className="text-yellow-500" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Sign in</h1>
            <p className="text-gray-400 text-sm">Welcome back!</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-900 border border-red-700 rounded-lg flex items-center space-x-2">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
              <span className="text-red-200 text-sm">{error}</span>
            </div>
          )}

          {/* Login Form */}
          <div className="space-y-6">
            {/* Email Input */}
            <div>
              <input
                type="email"
                placeholder="Email address"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
              />
            </div>

            {/* Password Input */}
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 pr-12 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
              >
                {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="form-checkbox h-4 w-4 text-yellow-600 rounded focus:ring-yellow-500 focus:ring-offset-gray-800 bg-gray-700 border-gray-600"
                />
                <span className="text-white text-sm">Keep me logged in</span>
              </label>
              <button
                onClick={handleForgotPassword}
                className="text-yellow-500 hover:text-yellow-400 text-sm transition-colors"
              >
                Forgot password?
              </button>
            </div>

            {/* Login Button */}
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-700 hover:to-yellow-800 disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-105"
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                  <span>Signing in...</span>
                </div>
              ) : (
                'Login'
              )}
            </button>

            {/* Social Login Options */}
            <div className="space-y-3">
              <SocialLoginButton
                label="Continue with Facebook"
                onClick={() => handleSocialLogin('facebook')}
              />
              <SocialLoginButton
                label="Continue with Google"
                onClick={() => handleSocialLogin('google')}
              />
            </div>

            {/* Create Account Link */}
            <div className="text-center">
              <span className="text-gray-400 text-sm">Don't have an account? </span>
              <button
                onClick={handleCreateAccount}
                className="text-yellow-500 hover:text-yellow-400 text-sm font-medium transition-colors"
              >
                Create
              </button>
            </div>
          </div>
        </div>

        {/* Footer Info */}
        <div className="text-center mt-6">
          <div className="bg-gray-800 bg-opacity-50 rounded-lg px-4 py-2 inline-block">
            <p className="text-gray-400 text-xs">walkthaducking.com</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;